// src/pages/Profile.jsx
import React, { useEffect, useState, useCallback } from "react";
import { auth, db } from "../utils/firebase";
import { doc, onSnapshot, updateDoc } from "firebase/firestore";
import { useNavigate } from "react-router-dom";
import "../styles/Profile.css";

const Profile = () => {
  const navigate = useNavigate();

  const [formData, setFormData] = useState({
    name: "",
    email: "",
    mobile: "",
  });

  const [editing, setEditing] = useState(false);
  const [loading, setLoading] = useState(true);
  const [successMsg, setSuccessMsg] = useState("");
  const [errorMsg, setErrorMsg] = useState("");

  // Load profile data (single Firestore subscription)
  useEffect(() => {
    const user = auth.currentUser;

    if (!user) {
      setErrorMsg("⚠️ User not authenticated.");
      setLoading(false);
      return;
    }

    const userRef = doc(db, "users", user.uid);

    const unsubscribe = onSnapshot(
      userRef,
      (docSnap) => {
        if (docSnap.exists()) {
          const data = docSnap.data();
          setFormData({
            name: data.name || "",
            email: data.email || user.email || "",
            mobile: data.mobile || "",
          });
          setErrorMsg("");
        } else {
          setErrorMsg("⚠️ Profile data not found.");
        }
        setLoading(false);
      },
      (error) => {
        console.error("Error loading profile:", error);
        setErrorMsg("❌ Failed to load profile.");
        setLoading(false);
      }
    );

    return () => unsubscribe();
  }, []);

  const handleChange = useCallback((e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  }, []);

  const handleUpdate = useCallback(async () => {
    setSuccessMsg("");
    setErrorMsg("");

    if (!formData.name.trim()) {
      setErrorMsg("⚠️ Name cannot be empty.");
      return;
    }

    const user = auth.currentUser;
    if (!user) {
      setErrorMsg("⚠️ User not authenticated.");
      return;
    }

    try {
      const userRef = doc(db, "users", user.uid);
      await updateDoc(userRef, {
        name: formData.name.trim(),
        mobile: formData.mobile.trim(),
      });

      setSuccessMsg("✅ Profile updated successfully.");
      setEditing(false);
    } catch (err) {
      console.error("Error updating profile:", err);
      setErrorMsg("❌ Failed to update profile.");
    }
  }, [formData]);

  const handleCancel = useCallback(() => {
    setEditing(false);
    setErrorMsg("");
    setSuccessMsg("");
  }, []);

  const handleLogout = useCallback(async () => {
    try {
      await auth.signOut();
      navigate("/login", { replace: true });
    } catch (err) {
      console.error("Logout error:", err);
      setErrorMsg("❌ Failed to logout.");
    }
  }, [navigate]);

  if (loading) {
    return (
      <div className="profile-page">
        <div className="loading-msg">🔄 Loading profile...</div>
      </div>
    );
  }

  return (
    <section className="profile-page">
      <div className="profile-card">
        <header className="profile-header">
          <h2>👤 My Profile</h2>
        </header>

        {successMsg && (
          <div className="success-msg" role="status">
            {successMsg}
          </div>
        )}
        {errorMsg && (
          <div className="error-msg" role="alert">
            {errorMsg}
          </div>
        )}

        <form
          className="profile-form"
          onSubmit={(e) => e.preventDefault()}
        >
          <div className="form-group">
            <label htmlFor="name">Full Name</label>
            <input
              id="name"
              type="text"
              name="name"
              value={formData.name}
              onChange={handleChange}
              disabled={!editing}
              placeholder="Enter your full name"
              autoComplete="name"
            />
          </div>

          <div className="form-group">
            <label htmlFor="email">Email Address</label>
            <input
              id="email"
              type="email"
              value={formData.email}
              disabled
              placeholder="Your email address"
              autoComplete="email"
            />
          </div>

          <div className="form-group">
            <label htmlFor="mobile">Mobile Number</label>
            <input
              id="mobile"
              type="tel"
              name="mobile"
              value={formData.mobile}
              onChange={handleChange}
              disabled={!editing}
              placeholder="Enter your mobile number"
              autoComplete="tel"
            />
          </div>

          {!editing ? (
            <div className="btn-group">
              <button
                type="button"
                className="edit-btn"
                onClick={() => setEditing(true)}
              >
                ✏️ Edit Profile
              </button>

              <button
                type="button"
                className="logout-btn"
                onClick={handleLogout}
              >
                🔒 Logout
              </button>
            </div>
          ) : (
            <div className="btn-group">
              <button
                type="button"
                className="save-btn"
                onClick={handleUpdate}
              >
                💾 Save
              </button>

              <button
                type="button"
                className="cancel-btn"
                onClick={handleCancel}
              >
                ❌ Cancel
              </button>
            </div>
          )}
        </form>
      </div>
    </section>
  );
};

export default Profile;
